import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { EditMode } from '@shared/edit.mode';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ClientAuthType,
  ClientAuthTypes,
  OrganizationTlsOptionsService,
  TlsOptionsData,
  TlsVersion,
  TlsVersions,
} from '@shared/services/organization-tls-options.service';
import { ToastrService } from '@shared/services/toastr.service';
import { CertInfo, extractCertInfo } from '@shared/utils/certificates';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { LabelComponent } from '@shared/components/label/label.component';
import { DateFormatPipe, TranslateLabelPipe } from '@shared/pipe/filter.pipe';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-add-edit-tls-options',
  templateUrl: './add-edit-tls-options.component.html',
  styleUrls: ['./add-edit-tls-options.component.scss'],
  imports: [FormsModule, ReactiveFormsModule, LabelComponent, DateFormatPipe, TranslatePipe, TranslateLabelPipe],
})
export class AddEditTlsOptionsComponent implements OnInit, OnDestroy {
  @Input() tlsOptionsUid: string;
  protected readonly ClientAuthTypes = ClientAuthTypes;

  tlsFormGroup: FormGroup;
  certFormControl: FormControl = new FormControl('');
  mode: EditMode;
  certInfo: CertInfo;
  certificateError: any;
  clientAuthTypesTable: ClientAuthType[];
  subscriptions: Subscription = new Subscription();
  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private toastr: ToastrService,
    private tlsOptionsService: OrganizationTlsOptionsService,
    private fb: FormBuilder,
  ) {
    this.clientAuthTypesTable = [...this.ClientAuthTypes];
  }

  ngOnInit(): void {
    this.initForm();
    this.initTlsOptions();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private emptyTlsOptions() {
    this.tlsFormGroup.reset({
      uid: '',
      name: '',
      minTlsVersion: 'TLS_1.2',
      maxTlsVersion: '',
      clientAuthType: '',
      clientAuthCaCerts: [],
    });
  }

  protected readonly extractCertInfo = extractCertInfo;

  protected readonly TlsVersions = TlsVersions;

  private initForm(): void {
    this.tlsFormGroup = this.fb.group({
      uid: new FormControl(''),
      name: new FormControl('', Validators.required),
      minTlsVersion: new FormControl('TLS_1.2', Validators.required),
      maxTlsVersion: new FormControl(''),
      clientAuthType: new FormControl('', Validators.required),
      clientAuthCaCerts: new FormControl<string[]>([]),
    });

    this.subscriptions.add(
      this.tlsFormGroup.get('clientAuthType')!.valueChanges.subscribe((value) => {
        const clientAuthCaCerts = this.tlsFormGroup.get('clientAuthCaCerts')!;
        if (value !== 'IgnoreCert') {
          clientAuthCaCerts.setValidators(Validators.required);
        } else {
          clientAuthCaCerts.clearValidators();
        }
        clientAuthCaCerts.updateValueAndValidity();
      }),
    );
    this.subscriptions.add(
      this.tlsFormGroup.get('minTlsVersion')!.valueChanges.subscribe((min) => {
        this.checkVersionComparison(min, this.tlsFormGroup.get('maxTlsVersion')!.value);
      }),
    );
    this.subscriptions.add(
      this.certFormControl.valueChanges.subscribe((cert) => {
        this.certInfo = null;
        this.certificateError = null;
        if (cert) {
          try {
            this.certInfo = extractCertInfo(cert);
          } catch (e) {
            this.certificateError = e.message;
          }
        }
      }),
    );
    this.subscriptions.add(
      this.tlsFormGroup.get('maxTlsVersion')!.valueChanges.subscribe((max) => {
        this.checkVersionComparison(this.tlsFormGroup.get('minTlsVersion')!.value, max);
      }),
    );
  }

  private loadTlsOptions(uid: string): void {
    this.subscriptions.add(
      this.tlsOptionsService.getTlsOptionsByUid(uid).subscribe((tlsOptions) => {
        this.tlsFormGroup.patchValue(tlsOptions);
      }),
    );
  }

  private navigateToOrganizationTlsOptions() {
    this.router.navigate(['../..'], { relativeTo: this.route, queryParams: { tab: 'tlsOptions' } });
  }

  private splittingTLSVersion(version: TlsVersion): number {
    return +version.split('_')[1];
  }

  checkVersionComparison(minTlsVersion: TlsVersion, maxTlsVersion: TlsVersion): void {
    if (!maxTlsVersion || !minTlsVersion) return;

    const minVersion: number = this.splittingTLSVersion(minTlsVersion);
    const maxVersion: number = this.splittingTLSVersion(maxTlsVersion);

    if (minVersion > maxVersion) {
      this.toastr.error('TlsOptions.minVersionCantBeGreaterThanMaxVersion');
    }
  }

  initTlsOptions() {
    const routeParams = this.route.snapshot.paramMap;
    const uid = routeParams.get('uid');
    if (uid) {
      this.mode = 'update';
      this.loadTlsOptions(uid);
    } else {
      this.mode = 'create';
      this.emptyTlsOptions();
    }
  }

  save() {
    if (this.tlsFormGroup.invalid) return;

    const { uid, maxTlsVersion, ...rest } = this.tlsFormGroup.value;
    const data = {
      ...rest,
      ...(maxTlsVersion && { maxTlsVersion }),
    };

    if (this.mode == 'create') {
      this.subscriptions.add(
        this.tlsOptionsService.createTlsOptions(data as TlsOptionsData).subscribe((tlsOptions) => {
          this.tlsFormGroup.patchValue(tlsOptions);
          this.mode = 'update';
          this.toastr.success();
          this.navigateToOrganizationTlsOptions();
        }),
      );
    } else {
      this.subscriptions.add(
        this.tlsOptionsService.updateTlsOptions(uid, data).subscribe((tlsOptions) => {
          this.tlsFormGroup.patchValue(tlsOptions);
          this.toastr.success();
          this.navigateToOrganizationTlsOptions();
        }),
      );
    }
  }

  cancel() {
    this.navigateToOrganizationTlsOptions();
  }

  addCertificate() {
    const control = this.tlsFormGroup.get('clientAuthCaCerts');
    control.patchValue([...control.value, this.certFormControl.value]);
    this.certFormControl.reset();
  }

  deleteCaCert(index: number) {
    const caCerts = this.tlsFormGroup.get('clientAuthCaCerts').value as string[];
    caCerts.splice(index, 1);
    this.tlsFormGroup.get('clientAuthCaCerts').setValue([...caCerts]);
  }

  allowedMinVersion = (version: TlsVersion): boolean => {
    const maxVersion = this.tlsFormGroup.get('maxTlsVersion').value;
    return !maxVersion || this.tlsVersionNumber(version) <= this.tlsVersionNumber(maxVersion);
  };

  allowedMaxVersion = (version: TlsVersion): boolean => {
    const minVersion = this.tlsFormGroup.get('minTlsVersion').value;
    return !minVersion || this.tlsVersionNumber(version) >= this.tlsVersionNumber(minVersion);
  };

  tlsVersionNumber(version: TlsVersion) {
    return Number.parseFloat(version.replace(/^TLS_/, ''));
  }
}
